import React from 'react';
import { AlertTriangle, Phone, RotateCcw } from 'lucide-react';

/**
 * ErrorBoundary — Catches JavaScript errors in any child component.
 * Shows a patient-safe fallback with emergency contact info.
 * CRITICAL: A white screen for a patient in distress is a safety issue.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="min-h-screen flex items-center justify-center bg-surface px-6"
          role="alert"
          aria-live="assertive"
        >
          <div className="max-w-md w-full text-center space-y-6">
            {/* Icon */}
            <div className="w-16 h-16 rounded-2xl bg-error/10 flex items-center justify-center mx-auto">
              <AlertTriangle className="w-8 h-8 text-error" aria-hidden="true" />
            </div>

            {/* Message */}
            <div>
              <h1 className="text-xl font-bold text-slate-900 mb-2">
                Something went wrong
              </h1>
              <p className="text-sm text-slate-500 leading-relaxed">
                We're sorry — the app ran into a problem. Your data is safe.
                Please try refreshing the page.
              </p>
            </div>

            {/* Emergency Banner */}
            <div className="bg-red-50 border border-red-200 rounded-2xl p-5 text-left">
              <p className="text-sm font-bold text-red-800 mb-2">
                If this is a medical emergency:
              </p>
              <a
                href="tel:911"
                className="flex items-center gap-3 px-4 py-3 bg-red-600 text-white rounded-xl font-bold text-sm hover:bg-red-700 transition-all justify-center"
                aria-label="Call 911 for emergency"
              >
                <Phone className="w-5 h-5" aria-hidden="true" />
                Call 911
              </a>
            </div>

            {/* Recovery actions */}
            <div className="space-y-3">
              <button
                onClick={this.handleReset}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-primary text-white rounded-2xl font-bold text-sm hover:bg-primary/90 transition-all"
              >
                <RotateCcw className="w-4 h-4" aria-hidden="true" />
                Try Again
              </button>
              <button
                onClick={() => { window.location.href = '/'; }}
                className="w-full px-5 py-3 bg-slate-100 text-slate-700 rounded-2xl font-bold text-sm hover:bg-slate-200 transition-all"
              >
                Go to Home
              </button>
            </div>

            {/* Helpline */}
            <p className="text-xs text-slate-500">
              Need help? Call{' '}
              <a href="tel:1-800-CARE-NOW" className="text-primary font-bold">
                1-800-CARE-NOW
              </a>
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
